import { V3SynthMarketKey, v3SynthKeyToV2Asset } from '@kwenta/sdk/constants'
import { formatDollars, formatNumber } from '@kwenta/sdk/utils'
import { wei } from '@synthetixio/wei'
import Image from 'next/image'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import DepositArrow from 'assets/svg/futures/deposit-arrow.svg'
import WithdrawArrow from 'assets/svg/futures/withdraw-arrow.svg'
import { FlexDivRowCentered } from 'components/layout/flex'
import Pill from 'components/Pill'
import Spacer from 'components/Spacer'
import Table, { TableHeader, TableNoResults } from 'components/Table'
import { CustomFontLabel } from 'components/Text/CustomFontLabel'
import { NO_VALUE } from 'constants/placeholder'
import useIsL2 from 'hooks/useIsL2'
import useNetworkSwitcher from 'hooks/useNetworkSwitcher'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectCollateralBalances } from 'state/futures/crossMargin/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectPrices } from 'state/prices/selectors'
import { formatV3AssetKey } from 'utils/futures'
import { SYNTH_ICONS } from 'utils/icons'

export default function CollateralBalancesTable({ mobile }: { mobile?: boolean }) {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { switchToL2 } = useNetworkSwitcher()
	const isL2 = useIsL2()

	const balances = useAppSelector(selectCollateralBalances)
	const prices = useAppSelector(selectPrices)

	const rows = useMemo(() => {
		return Object.keys(balances).map((b) => {
			const key = b as V3SynthMarketKey
			const balance = wei(balances[key] ?? 0)
			const asset = v3SynthKeyToV2Asset(key)
			const price = asset === 'sUSD' ? wei(1) : prices[asset].offChain
			return {
				asset: key,
				balance: balance,
				dollarValueFormatted: price ? formatDollars(balance.mul(price)) : '-',
				formattedBalance: formatNumber(balance, { suggestDecimals: true }),
			}
		})
	}, [balances, prices])

	const onClickDeposit = useCallback(
		(asset: V3SynthMarketKey) => {
			dispatch(
				setOpenModalWithParams({
					type: 'futures_deposit_withdraw_cross_margin',
					params: { asset, transferType: 'deposit' },
				})
			)
		},
		[dispatch]
	)

	const onClickWithdraw = useCallback(
		(asset: V3SynthMarketKey) => {
			dispatch(
				setOpenModalWithParams({
					type: 'futures_deposit_withdraw_cross_margin',
					params: { asset, transferType: 'withdraw' },
				})
			)
		},
		[dispatch]
	)

	return (
		<Container>
			<Table
				data={rows}
				highlightRowsOnHover
				noBottom={true}
				rounded={false}
				noResultsMessage={
					!isL2 ? (
						<TableNoResults>
							{t('common.l2-cta')}
							<div onClick={switchToL2}>{t('homepage.l2.cta-buttons.switch-l2')}</div>
						</TableNoResults>
					) : (
						<TableNoResults>{t('futures.market.user.open-orders.table.no-result')}</TableNoResults>
					)
				}
				columns={[
					{
						header: () => (
							<TableHeader>{t('futures.market.user.balances.table.asset')}</TableHeader>
						),
						accessorKey: 'market',
						cell: (cellProps) => {
							const icon = SYNTH_ICONS[cellProps.row.original.asset]
							return (
								<>
									{icon && (
										<>
											<Image src={icon} width={20} height={20} alt={cellProps.row.original.asset} />
											<Spacer width={10} />
										</>
									)}
									{formatV3AssetKey(cellProps.row.original.asset)}
								</>
							)
						},
						enableSorting: true,
					},
					{
						header: () => (
							<TableHeader>{t('futures.market.user.balances.table.balance-native')}</TableHeader>
						),
						accessorKey: 'balance',
						cell: (cellProps) => {
							return <CustomFontLabel text={cellProps.row.original.formattedBalance ?? NO_VALUE} />
						},
						enableSorting: true,
					},
					{
						header: () => (
							<TableHeader>{t('futures.market.user.balances.table.balance-usd')}</TableHeader>
						),
						accessorKey: 'dollarValue',
						cell: (cellProps) => {
							return <CustomFontLabel text={cellProps.row.original.dollarValueFormatted} />
						},
						enableSorting: true,
					},
					{
						header: () => (
							<TableHeader>{t('futures.market.user.balances.table.actions')}</TableHeader>
						),
						accessorKey: 'actions',
						size: mobile ? 70 : undefined,
						cell: (cellProps) => {
							return (
								<FlexDivRowCentered>
									<Pill onClick={() => onClickDeposit(cellProps.row.original.asset)}>
										{mobile ? <DepositArrow height={18} /> : 'Deposit'}
									</Pill>
									<Spacer width={14} />
									<Pill onClick={() => onClickWithdraw(cellProps.row.original.asset)}>
										{mobile ? <WithdrawArrow height={18} /> : 'Withdraw'}
									</Pill>
								</FlexDivRowCentered>
							)
						},
					},
				]}
			/>
		</Container>
	)
}

const Container = styled.div`
	height: calc(100% - 40px);
	overflow: scroll;
`
